package com.neurobs.ccabserver.administrator.components

import com.neurobs.ccab.{AccountSubjectResponse, CCABServer, Sex, SubjectCreateData, SubjectIdentityData, SubjectUpdateData}
import com.neurobs.errors._
import com.neurobs.react._
import diode.ActionResult.ModelUpdate
import diode._
import slinky.core.FunctionalComponent
import slinky.core.annotations.react

import scala.concurrent.ExecutionContext.Implicits.global

/**
 * Form that displays Subject details used by subject create and subject edit forms
 */
object SubjectForm {

	case class Model
	(
		anonymous: CheckboxControlWrapper,
		firstName: EditControlWrapper,
		lastName: EditControlWrapper,
		birthdate: EditControlWrapper,
		sex: SelectState[Sex.Value, Sex.Value],
		localId: EditControlWrapper
	) {
		private def getBirthYear: Result[Int] = {
			val thisYear = java.time.LocalDate.now(java.time.ZoneId.of("GMT")).getYear
			birthdate.intValue() match {
				case None => Left("The birth year is not valid.")
				case Some(year) =>
					if ((year > thisYear) || (year < 1915)) Left("The birth year is not valid.")
					else Right(year)
			}
		}

		def get: Result[SubjectIdentityData] = {
			val sexResult = toResult(sex.value, noValueMessage = "The sex value must be selected.")
			if (anonymous.value) {
				for {
					birthYear <- getBirthYear
					s <- sexResult
					id <- localId.getNonEmpty(name = "Local ID")
				} yield SubjectIdentityData(
					firstName = None,
					lastName = None,
					birthdate = None,
					birthYear = birthYear,
					sex = s,
					localID = Some(id)
				)
			}
			else {
				for {
					bd <- birthdate.getDate(name = "Birthdate")
					s <- sexResult
					fn <- firstName.getNonEmpty(name = "First Name")
					ln <- lastName.getNonEmpty(name = "Last Name")
				} yield SubjectIdentityData(
					firstName = Some(fn),
					lastName = Some(ln),
					birthdate = Some(bd),
					birthYear = bd.date.getYear,
					sex = s,
					localID = localId.optionValue()
				)
			}
		}
	}

	object Model {
		def apply(data: Option[SubjectIdentityData])(implicit factory: ControlWrapperFactory): Model = {
			Model(
				anonymous = factory.checkbox(data.map(_.lastName.isEmpty).getOrElse(false)),
				firstName = factory.edit(data.flatMap(_.firstName).getOrElse("")),
				lastName = factory.edit(data.flatMap(_.lastName).getOrElse("")),
				birthdate = factory.edit(data.map(d => d.birthdate.map(_.toString).getOrElse(d.birthYear.toString)).getOrElse("")),
				sex = SelectState.typed[Sex.Value](items = Sex.values.toSeq, value = data.map(_.sex), noSelectOption = true, toCaption = _.toString),
				localId = factory.edit(data.flatMap(_.localID).getOrElse(""))
			)
		}
	}

	case object AnonymousChanged extends Action
	class AppCircuit(val initialModel: Model) extends Circuit[Model] {
		override val actionHandler: HandlerFunction = (model, action) => action match {
			case AnonymousChanged => Some(ModelUpdate(model.copy()))
			case _ => None
		}
	}

	@react object View {
		case class Props(model: Model, dispatcher: Dispatcher)

		val boxWidth = 20

		val component = FunctionalComponent[Props] { props =>
			val model = props.model
			val anonymous = model.anonymous.value
			FormTable(
				FormTableRow(
					"Anonymous:",
					Checkbox(model.anonymous, onChange = Some(() => props.dispatcher(AnonymousChanged)))
				).withKey(key = "anon"),
				if (!anonymous)
					FormTableRow(
						"First name:",
						Edit(model.firstName, emWidth = boxWidth)
					).withKey(key = "fn")
				else null,
				if (!anonymous)
					FormTableRow(
						"Last name:",
						Edit(model.lastName, emWidth = boxWidth)
					).withKey(key = "ln")
				else null,
				FormTableRow(
					if (anonymous) "Birth Year:" else "Birthdate (YYYY-MM-DD):",
					Edit(model.birthdate, emWidth = boxWidth)
				).withKey(key = "bd"),
				FormTableRow(
					"Sex:",
					Select(model.sex.generateProperties(), emWidth = Some(boxWidth))
				).withKey(key = "s"),
				FormTableRow(
					if (anonymous) "Organization ID:" else "Organization ID (optional):",
					Edit(model.localId, emWidth = boxWidth)
				).withKey(key = "id")
			)
		}
	}

	def createState(data: Option[SubjectIdentityData])(implicit factory: ControlWrapperFactory): CircuitState[Model] = {
		CircuitState[Model](() => Model(data), new AppCircuit(_))
	}
}

/**
 * Dialog for creating a new subject
 */
@react object CreateSubjectDialog {
	type ResultType = FormProcessResultValue[AccountSubjectResponse]

	/**
	 * @param server CCABServer to call
	 * @param accountId Account to create the account-subject for
	 * @param dataPassword Data password for this account
	 * @param onFinished Call back receiving new subject data, or None if cancelled
	 */
	case class Props
	(
		server: CCABServer,
		accountId: Long,
		dataPassword: String,
		onFinished: Option[AccountSubjectResponse] => Unit,
		showTitle: Boolean = true
	)

	val component = FunctionalComponent[Props] { props =>
		val state = SubjectForm.createState(None)(ControlWrapperFactoryReact)

		def process(): Result[Either[FormProcessResult, FormProcessor]] = {
			state.value.get map { idData =>
				val data = SubjectCreateData(data = idData, dataPassword = props.dataPassword, reportingId = None)
				val request = props.server.createSubject(accountId = props.accountId, data = data)
				Right(FormProcessorRequest(request))
			}
		}

		FormDialog(
			title = if (props.showTitle) Some("Create Subject") else None,
			submitButtonCaption = "Create",
			customView = Some(SubjectForm.View(state.value, state.dispatcher)),
			emWidth = Some(40),
			process = () => process(),
			onFormCancel = Some(() => props.onFinished(None)),
			onSuccess = res => props.onFinished(Some(res.asInstanceOf[ResultType].value))
		)
	}
}

/**
 * Dialog for editing an existing subject
 */
@react object EditSubjectDialog {
	/**
	 * @param server CCABServer to call
	 * @param subject Subject to be edited
	 * @param dataPassword Data password for the account associated with the account subject
	 * @param onFinished Call back receiving whether the subject was updated or not
	 */
	case class Props(server: CCABServer, subject: AccountSubjectResponse, dataPassword: String, onFinished: Boolean => Unit)

	val component = FunctionalComponent[Props] { props =>
		val state = SubjectForm.createState(props.subject.identity)(ControlWrapperFactoryReact)

		def process(): Result[Either[FormProcessResult, FormProcessor]] = {
			state.value.get map { idData =>
				val data = SubjectUpdateData(data = idData, dataPassword = props.dataPassword)
				val request = props.server.updateSubject(accountSubjectId = props.subject.id, data = data)
				Right(FormProcessorRequest(request))
			}
		}

		FormDialog(
			title = Some("Edit Subject"),
			submitButtonCaption = "Save",
			customView = Some(SubjectForm.View(state.value, state.dispatcher)),
			emWidth = Some(40),
			process = () => process(),
			onFormCancel = Some(() => props.onFinished(false)),
			onSuccess = _ => props.onFinished(true)
		)
	}
}